import React from 'react';
import axios from 'axios';
import Lottie from 'lottie-react';
import './WeatherDisplay.css';
// Import day and night animations
import clear_day from './animations/sun.json';
import clear_night from './animations/moon.json';
import cloudy_day from './animations/sun-cloud.json';
import cloudy_night from './animations/moon-cloud.json';
import stormy_day from './animations/storm.json';
import stormy_night from './animations/storm.json';
import windy_day from './animations/windy.json';
import windy_night from './animations/windy.json';
import rain_day from './animations/sun-rain.json';
import rain_night from './animations/moon-rain.json';
import defaultAnim from './animations/cloud.json';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export interface WeatherData {
  temperature: number;
  wind_speed: number;
  condition: string;
  fetched_at: string;
  sunrise: string | null;
  sunset: string | null;
  precipitation: number | null;
}

interface WeatherDisplayProps {
  weather: WeatherData | null;
}

export const FetchWeatherForSightseeing = (
  sightseeingId: number,
  setWeatherData: React.Dispatch<React.SetStateAction<WeatherData | null>>,
  setWeatherLoading: React.Dispatch<React.SetStateAction<boolean>>,
): void => {
  setWeatherLoading(true);
  axios
    .get(`${backendUrl}/worlddata/weather/get-weather-cache/${sightseeingId}`)
    .then(() => axios.get(`${backendUrl}/worlddata/weather/get-weather-data/${sightseeingId}`))
    .then((res) => {
      if (res.data.weather) {
        setWeatherData(res.data.weather);
      } else {
        setWeatherData(null);
      }
    })
    .catch((err) => {
      console.error('Error fetching weather:', err);
      setWeatherData(null);
    })
    .finally(() => {
      setWeatherLoading(false);
    });
};

function isDaytime(fetchedAt: string, sunrise: string | null, sunset: string | null): boolean {
  if (!sunrise || !sunset) return true;
  const now = new Date(fetchedAt);
  return now >= new Date(sunrise) && now < new Date(sunset);
}

function getAnimation(condition: string, isDay: boolean): object {
  switch (condition) {
    case 'clear':
      return isDay ? clear_day : clear_night;
    case 'cloudy':
      return isDay ? cloudy_day : cloudy_night;
    case 'rain':
      return isDay ? rain_day : rain_night;
    case 'storm':
      return isDay ? stormy_day : stormy_night;
    case 'windy':
      return isDay ? windy_day : windy_night;
    default:
      return defaultAnim;
  }
}

export function WeatherDisplay({ weather }: WeatherDisplayProps): JSX.Element | null {
  if (!weather) return null;

  const { temperature, wind_speed, condition, fetched_at, sunrise, sunset, precipitation } = weather;

  const isDay = isDaytime(fetched_at, sunrise, sunset);
  const animationData = getAnimation(condition, isDay);
  const updatedAtUtc = new Date(fetched_at).toLocaleString('en-GB', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const updatedAtLocal = new Date(fetched_at).toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  return (
    <div className="weather-card">
      <div className="weather-header">
        <div className="weather-animation">
          <Lottie animationData={animationData} loop={true} />
        </div>
        <h3>Current Weather</h3>
      </div>
      <div className="weather-content">
        <div className="weather-details">
          <div>Condition: {condition || 'N/A'}</div>
          <div>Wind Speed: {wind_speed} km/h</div>
          {precipitation !== null && <div>Precipitation: {precipitation} mm</div>}
          <div className="weather-timestamp">Updated: {updatedAtUtc} UTC ({updatedAtLocal} Local)</div>
        </div>
        <div className="weather-temperature">{temperature}°C</div>
      </div>
    </div>
  );
}

export default WeatherDisplay;
